/**
 * VoiceDeckEntry — dictate a deck card-by-card using the browser's Web Speech API.
 *
 * Flow:
 *   1. Choose a commander — its color identity filters all subsequent suggestions
 *   2. Click "Start Listening"
 *   3. Say a card name (optionally prefixed with a number word or digit)
 *      • Saying just a number stores it as the qty for the next utterance
 *      • Saying a number while a card is pending updates that card's qty
 *   4. Top fuzzy-matched suggestions appear:
 *      • Enter/Space or click to confirm
 *      • Backspace to dismiss and retry
 *      • Type any character to jump into correction mode (first char is kept)
 *   5. Say "undo" to remove the last card, "done" to finish
 *   6. Click "Done — Import X Cards" to hand the decklist back to the parent
 *
 * State is persisted to localStorage so accidental navigation doesn't lose progress.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { cards } from '@/lib/api';
import type { AutocompleteCard } from '@/lib/api';
import { CardAutocomplete } from './CardAutocomplete';

// SpeechRecognition types not in all TS DOM libs
declare global {
  interface Window {
    SpeechRecognition: new () => ISpeechRecognition;
    webkitSpeechRecognition: new () => ISpeechRecognition;
  }
}
interface ISpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: { transcript: string; confidence: number };
}
interface ISpeechRecognitionEvent {
  readonly resultIndex: number;
  readonly results: { length: number; [index: number]: ISpeechRecognitionResult };
}
interface ISpeechRecognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: ISpeechRecognitionEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const NUMBER_WORDS: Record<string, number> = {
  one: 1, two: 2, three: 3, four: 4, five: 5,
  six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15,
  sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20,
  thirty: 30, forty: 40,
};

/** Returns the number if the ENTIRE text is a number word or digit string, else null. */
function parsePureNumber(text: string): number | null {
  const t = text.trim().toLowerCase();
  if (NUMBER_WORDS[t] !== undefined) return NUMBER_WORDS[t];
  const n = parseInt(t, 10);
  if (!isNaN(n) && String(n) === t) return n;
  return null;
}

function parseQtyAndName(raw: string): { qty: number; name: string } {
  const words = raw.trim().split(/\s+/);
  const first = words[0]?.toLowerCase() ?? '';
  const wordQty = NUMBER_WORDS[first];
  if (wordQty !== undefined && words.length > 1) {
    return { qty: wordQty, name: words.slice(1).join(' ') };
  }
  const numMatch = /^(\d+)\s+(.+)$/.exec(raw.trim());
  if (numMatch) {
    return { qty: parseInt(numMatch[1], 10), name: numMatch[2] };
  }
  return { qty: 1, name: raw.trim() };
}

// ── Persistence ───────────────────────────────────────────────────────────────

const STORAGE_KEY = 'voice-deck-entry';

interface SavedVoiceState {
  commander: string;
  commanderCI: string[];
  confirmed: ConfirmedCard[];
}

function loadSavedState(): SavedVoiceState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedVoiceState;
    if (!parsed.commander) return null;
    return parsed;
  } catch { return null; }
}
function saveState(s: SavedVoiceState) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}
function clearSavedState() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface ConfirmedCard { name: string; qty: number; }
interface PendingCard { qty: number; name: string; }

export interface VoiceDeckEntryProps {
  onComplete: (decklist: string, commanderName: string) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function VoiceDeckEntry({ onComplete }: VoiceDeckEntryProps) {
  const supported =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  // ── Step 1: commander ─────────────────────────────────────────────────────
  const [commanderInput, setCommanderInput] = useState('');
  const [commander, setCommander] = useState<string | null>(null);
  const [commanderCI, setCommanderCI] = useState<string[]>([]);
  const [commanderLoading, setCommanderLoading] = useState(false);
  const [wasRestored, setWasRestored] = useState(false);

  // ── Step 2: dictation ─────────────────────────────────────────────────────
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [pending, setPending] = useState<PendingCard | null>(null);
  const [pendingQty, setPendingQty] = useState<number | null>(null); // qty heard before card name
  const [suggestionsLoaded, setSuggestionsLoaded] = useState(false);
  const [suggestions, setSuggestions] = useState<AutocompleteCard[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [confirmed, setConfirmed] = useState<ConfirmedCard[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState(false);
  const [correctionValue, setCorrectionValue] = useState('');
  const [quickAddValue, setQuickAddValue] = useState('');
  const quickAddRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Refs mirror state for use in event-handler closures
  const listeningRef = useRef(false);
  const pendingRef = useRef<PendingCard | null>(null);
  const pendingQtyRef = useRef<number | null>(null);
  const suggestionsRef = useRef<AutocompleteCard[]>([]);
  const selectedIdxRef = useRef(0);
  const correctingRef = useRef(false);
  const commanderCIRef = useRef<string[]>([]);

  const recognitionRef = useRef<ISpeechRecognition | null>(null);
  const doneRef = useRef<HTMLButtonElement>(null);
  const confirmedEndRef = useRef<HTMLDivElement>(null);
  const sessionStartRef = useRef<number | null>(null);

  useEffect(() => { listeningRef.current = listening; }, [listening]);
  useEffect(() => { pendingRef.current = pending; }, [pending]);
  useEffect(() => { pendingQtyRef.current = pendingQty; }, [pendingQty]);
  useEffect(() => { suggestionsRef.current = suggestions; }, [suggestions]);
  useEffect(() => { selectedIdxRef.current = selectedIdx; }, [selectedIdx]);
  useEffect(() => { correctingRef.current = correcting; }, [correcting]);
  useEffect(() => { commanderCIRef.current = commanderCI; }, [commanderCI]);

  // ── Restore saved state on mount ──────────────────────────────────────────

  useEffect(() => {
    const saved = loadSavedState();
    if (!saved) return;
    setCommander(saved.commander);
    setCommanderInput(saved.commander);
    const ci = saved.commanderCI ?? [];
    setCommanderCI(ci);
    commanderCIRef.current = ci;
    setConfirmed(saved.confirmed?.length ? saved.confirmed : [{ name: saved.commander, qty: 1 }]);
    setWasRestored(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist on change ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!commander) return;
    saveState({ commander, commanderCI, confirmed });
  }, [commander, commanderCI, confirmed]);

  // Auto-scroll confirmed list
  useEffect(() => {
    confirmedEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [confirmed]);

  // ── Commander confirm ─────────────────────────────────────────────────────

  const confirmCommander = useCallback(async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCommanderLoading(true);
    try {
      const detail = await cards.getDetail(trimmed);
      const ci = detail.color_identity ?? [];
      setCommanderCI(ci);
      commanderCIRef.current = ci;
    } catch {
      setCommanderCI([]);
      commanderCIRef.current = [];
    }
    setCommanderLoading(false);
    setCommander(trimmed);
    setConfirmed([{ name: trimmed, qty: 1 }]);
    setWasRestored(false);
  }, []);

  // ── Start Over ────────────────────────────────────────────────────────────

  const handleStartOver = useCallback(() => {
    clearSavedState();
    listeningRef.current = false;
    recognitionRef.current?.abort();
    recognitionRef.current = null;
    setListening(false);
    setInterim('');
    setCommander(null);
    setCommanderCI([]);
    setCommanderInput('');
    setConfirmed([]);
    setPending(null);
    pendingRef.current = null;
    setPendingQty(null);
    pendingQtyRef.current = null;
    setSuggestions([]);
    setSuggestionsLoaded(false);
    setCorrecting(false);
    setError(null);
    setWasRestored(false);
  }, []);

  // ── Shared add-or-increment helper ───────────────────────────────────────

  const addToConfirmed = useCallback((name: string, qty: number) => {
    setConfirmed(prev => {
      const existing = prev.findIndex(c => c.name === name);
      if (existing >= 0) {
        const next = [...prev];
        next[existing] = { name, qty: next[existing].qty + qty };
        return next;
      }
      return [...prev, { name, qty }];
    });
  }, []);

  // ── Confirm pending card ──────────────────────────────────────────────────

  const confirmCurrent = useCallback((overrideName?: string): boolean => {
    const pend = pendingRef.current;
    if (!pend) return false;
    const name = overrideName ?? suggestionsRef.current[selectedIdxRef.current]?.name;
    if (!name) return false;
    pendingRef.current = null;
    addToConfirmed(name, pend.qty);
    setPending(null);
    setSuggestions([]);
    setSuggestionsLoaded(false);
    setSelectedIdx(0);
    setCorrecting(false);
    setInterim('');
    return true;
  }, [addToConfirmed]);

  // ── Recognition ──────────────────────────────────────────────────────────

  const startRecognition = useCallback(() => {
    if (!supported) return;
    if (recognitionRef.current) {
      try { recognitionRef.current.start(); return; } catch { /* fall through */ }
    }

    const SR = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) {
          const text = r[0].transcript.trim();
          const norm = text.toLowerCase().replace(/[.,!?]$/, '');

          // ── Voice commands ─────────────────────────────────────────
          if (norm === 'undo' || norm === 'remove') {
            setConfirmed(prev => prev.length <= 1 ? prev : prev.slice(0, -1));
            return;
          }
          if (norm === 'done' || norm === 'finish') {
            doneRef.current?.focus();
            return;
          }
          if ((norm === 'yes' || norm === 'confirm' || norm === 'next') && pendingRef.current) {
            const ok = confirmCurrent();
            if (ok) return;
          }

          // ── Pure number — update pending qty or store for next card ─
          const pureNum = parsePureNumber(norm);
          if (pureNum !== null) {
            if (pendingRef.current) {
              // Update the qty of the card currently awaiting confirmation
              const updated = { ...pendingRef.current, qty: pureNum };
              pendingRef.current = updated;
              setPending(updated);
            } else {
              // Store qty; next card utterance will use it
              pendingQtyRef.current = pureNum;
              setPendingQty(pureNum);
            }
            return;
          }

          // ── New card ───────────────────────────────────────────────
          const parsed = parseQtyAndName(text);
          // Apply any qty said in a prior utterance
          const effectiveQty = pendingQtyRef.current !== null ? pendingQtyRef.current : parsed.qty;
          pendingQtyRef.current = null;
          setPendingQty(null);

          if (!parsed.name) return;

          pendingRef.current = { qty: effectiveQty, name: parsed.name };
          rec.stop();
          setPending({ qty: effectiveQty, name: parsed.name });
          setSelectedIdx(0);
          setSuggestions([]);
          setSuggestionsLoaded(false);
          setCorrecting(false);
          setInterim('');

          const ci = commanderCIRef.current;
          const ciParam = ci.length ? ci.join('') : undefined;
          void cards.voiceMatch(parsed.name, ciParam, 6).then(result => {
            setSuggestions(result.cards);
            setSuggestionsLoaded(true);
          }).catch(() => {
            setSuggestions([]);
            setSuggestionsLoaded(true);
          });
        } else {
          interimText += r[0].transcript;
        }
      }
      setInterim(interimText);
    };

    rec.onerror = (event) => {
      if (event.error === 'no-speech') return;
      setError(event.error === 'not-allowed'
        ? 'Microphone access denied. Please allow mic access and try again.'
        : `Speech error: ${event.error}`);
      listeningRef.current = false;
      setListening(false);
    };

    rec.onend = () => {
      if (listeningRef.current && !pendingRef.current) {
        try { rec.start(); } catch { /* stopped externally */ }
      }
    };

    recognitionRef.current = rec;
    try {
      rec.start();
    } catch {
      setError('Failed to start voice recognition. Try reloading the page.');
      listeningRef.current = false;
      setListening(false);
    }
  }, [supported, confirmCurrent]);

  const handleStartListening = useCallback(() => {
    setError(null);
    listeningRef.current = true;
    setListening(true);
    if (sessionStartRef.current === null) {
      sessionStartRef.current = Date.now();
    }
    startRecognition();
  }, [startRecognition]);

  const handleStopListening = useCallback(() => {
    listeningRef.current = false;
    setListening(false);
    setInterim('');
    recognitionRef.current?.abort();
    recognitionRef.current = null;
  }, []);

  const confirmAndResume = useCallback((overrideName?: string) => {
    const ok = confirmCurrent(overrideName);
    if (ok && listeningRef.current) {
      setTimeout(() => {
        if (listeningRef.current && !pendingRef.current) startRecognition();
      }, 100);
    }
  }, [confirmCurrent, startRecognition]);

  // ── Keyboard handler (pending row) ────────────────────────────────────────
  // Backspace = retry utterance. Escape NOT handled here (suppressed at dialog level).
  // Printable key = jump to correction mode with that character as the start.

  useEffect(() => {
    if (!pending) return;
    const handler = (e: KeyboardEvent) => {
      if (correctingRef.current) return;
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          confirmAndResume();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIdx(i => Math.min(i + 1, suggestionsRef.current.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIdx(i => Math.max(i - 1, 0));
          break;
        case 'Backspace':
          e.preventDefault();
          pendingRef.current = null;
          setPending(null);
          setSuggestions([]);
          setSuggestionsLoaded(false);
          if (listeningRef.current) startRecognition();
          break;
        default:
          // Jump to correction mode; the keypress that triggered this will naturally
          // type into the now-focused input — no need to pre-populate.
          if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
            setCorrectionValue('');
            setCorrecting(true);
          }
      }
    };
    document.addEventListener('keydown', handler, { capture: true });
    return () => document.removeEventListener('keydown', handler, { capture: true });
  }, [pending, confirmAndResume, startRecognition]);

  // ── Quick-add: focus input on printable key when nothing is pending ───────
  // Uses pendingRef (not pending state) to avoid re-registering on every utterance.

  useEffect(() => {
    if (!commander) return;
    const handler = (e: KeyboardEvent) => {
      if (pendingRef.current) return;
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'BUTTON') return;
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        quickAddRef.current?.focus();
        // Don't preventDefault — the character should type naturally into the input
      }
    };
    document.addEventListener('keydown', handler, { capture: true });
    return () => document.removeEventListener('keydown', handler, { capture: true });
  }, [commander]);

  // ── Cleanup ───────────────────────────────────────────────────────────────

  useEffect(() => () => {
    listeningRef.current = false;
    recognitionRef.current?.abort();
  }, []);

  // ── Quick-add confirm ─────────────────────────────────────────────────────

  const handleQuickAddKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key !== 'Enter') return;
    const name = quickAddValue.trim();
    if (!name) return;
    e.preventDefault();
    addToConfirmed(name, 1);
    setQuickAddValue('');
  }, [quickAddValue, addToConfirmed]);

  // ── Done ──────────────────────────────────────────────────────────────────

  const handleDone = () => {
    if (confirmed.length === 0) return;
    clearSavedState();
    if (sessionStartRef.current !== null) {
      const elapsedMs = Date.now() - sessionStartRef.current;
      const totalMs = Math.round(elapsedMs / 1000);
      const mins = Math.floor(totalMs / 60);
      const secs = totalMs % 60;
      const elapsed = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
      const nonCmdCards = confirmed.slice(1); // exclude commander row
      const totalQty = nonCmdCards.reduce((s, c) => s + c.qty, 0);
      console.info(
        `[VoiceDeckEntry] Session complete: ${nonCmdCards.length} unique cards, ` +
        `${totalQty} total copies, elapsed ${elapsed}`
      );
    }
    onComplete(confirmed.map(c => `${c.qty} ${c.name}`).join('\n'), commander ?? '');
  };

  // ── Unsupported ───────────────────────────────────────────────────────────

  if (!supported) {
    return (
      <div className="text-center py-8 flex flex-col gap-3 items-center">
        <span className="material-icons text-4xl text-on-surface-variant">mic_off</span>
        <p className="text-base text-on-surface-variant">Voice input isn't supported in this browser.</p>
        <p className="text-sm text-on-surface-variant opacity-70">
          Try Chrome, Edge, or Safari (iOS 14.5+).
        </p>
      </div>
    );
  }

  const totalCards = confirmed.reduce((s, c) => s + c.qty, 0);
  const ciColors = commanderCI.length ? commanderCI : null;

  // ── Step 1: Commander selection ───────────────────────────────────────────

  if (!commander) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-on-surface-variant leading-relaxed">
          Start by choosing your commander. Their color identity will filter suggestions
          so you only see cards your deck can legally run.
        </p>
        <div
          className="flex gap-2 items-start"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && commanderInput.trim() && !commanderLoading)
              void confirmCommander(commanderInput);
          }}
        >
          <div className="flex-1">
            <CardAutocomplete
              value={commanderInput}
              onChange={setCommanderInput}
              placeholder="Commander name…"
              autoFocus
              inputClassName="w-full bg-surface border border-outline-variant rounded-sm text-on-surface text-sm py-2 px-3 outline-none focus:border-primary"
            />
          </div>
          <button
            type="button"
            onClick={() => { if (commanderInput.trim()) void confirmCommander(commanderInput); }}
            disabled={!commanderInput.trim() || commanderLoading}
            className="py-2 px-4 bg-primary text-on-primary border-none rounded-sm text-sm font-semibold cursor-pointer disabled:opacity-50 whitespace-nowrap"
          >
            {commanderLoading ? 'Loading…' : 'Set Commander'}
          </button>
        </div>
      </div>
    );
  }

  // ── Step 2: Dictation ─────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-4">

      {/* Restored-session notice */}
      {wasRestored && (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-md text-sm"
          style={{ background: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <span className="material-icons text-base leading-none text-primary">history</span>
          <span className="text-on-surface-variant flex-1">
            Restored {totalCards} card{totalCards !== 1 ? 's' : ''} from your previous session.
          </span>
          <button type="button" onClick={handleStartOver}
            className="text-xs text-error-text bg-transparent border-none cursor-pointer hover:underline">
            Start Over
          </button>
        </div>
      )}

      {/* Commander badge */}
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm"
        style={{ background: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
      >
        <span className="material-icons text-base leading-none text-on-surface-variant">person</span>
        <span className="font-medium text-on-surface">{commander}</span>
        {ciColors && (
          <span className="text-xs text-on-surface-variant opacity-70">[{ciColors.join('')}]</span>
        )}
        <button type="button" onClick={handleStartOver}
          className="ml-auto text-xs text-on-surface-variant hover:text-error-text bg-transparent border-none cursor-pointer">
          Start Over
        </button>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={listening ? handleStopListening : handleStartListening}
          disabled={!!pending}
          className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border-none cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: listening ? 'var(--error-text)' : 'var(--primary)',
            color: listening ? '#fff' : 'var(--on-primary)',
          }}
        >
          <span className="material-icons text-base leading-none">{listening ? 'mic_off' : 'mic'}</span>
          {listening ? 'Stop' : 'Start Listening'}
        </button>
        <div className="flex items-center gap-3 text-sm text-on-surface-variant tabular-nums">
          {pendingQty !== null && (
            <span className="text-primary font-semibold">{pendingQty}× …</span>
          )}
          {totalCards} card{totalCards !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Quick-add input — always visible, catches stray keypresses to prevent global hotkey leakage */}
      {!pending && (
        <CardAutocomplete
          inputRef={quickAddRef}
          value={quickAddValue}
          onChange={setQuickAddValue}
          placeholder="Or type a card name…"
          tabComplete
          colorIdentity={ciColors ?? undefined}
          wrapperClassName="w-full"
          inputClassName="w-full bg-surface border border-outline-variant rounded-sm text-on-surface text-sm py-2 px-3 outline-none focus:border-primary"
          onKeyDown={handleQuickAddKeyDown}
        />
      )}

      {/* Error */}
      {error && <div className="text-error-text p-3 bg-status-error-bg rounded-sm text-sm">{error}</div>}

      {/* Live interim transcript */}
      {listening && interim && !pending && (
        <div className="text-sm text-on-surface-variant italic px-1 animate-pulse">"{interim}"</div>
      )}

      {/* Pending confirmation */}
      {pending && (
        <div
          className="rounded-md p-3 flex flex-col gap-2"
          style={{ background: 'var(--surface-container)', border: '2px solid var(--primary)' }}
        >
          <div className="flex items-center justify-between flex-wrap gap-1">
            <span className="text-xs text-on-surface-variant uppercase tracking-wider font-semibold">
              Heard: "{pending.name}"{pending.qty > 1 ? ` ×${pending.qty}` : ''}
            </span>
            <span className="text-xs text-on-surface-variant opacity-50">
              ↑↓ cycle · Enter confirm · Backspace retry
            </span>
          </div>

          {correcting ? (
            <div className="flex gap-2 items-start">
              <div className="flex-1">
                <CardAutocomplete
                  value={correctionValue}
                  onChange={setCorrectionValue}
                  placeholder="Type card name…"
                  autoFocus
                  tabComplete
                  colorIdentity={ciColors ?? undefined}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && correctionValue.trim()) {
                      e.preventDefault();
                      confirmAndResume(correctionValue.trim());
                    }
                  }}
                />
              </div>
              <button type="button"
                onClick={() => { if (correctionValue.trim()) confirmAndResume(correctionValue.trim()); }}
                disabled={!correctionValue.trim()}
                className="btn-primary py-1.5 px-3 text-sm whitespace-nowrap">
                Add
              </button>
              <button type="button" onClick={() => setCorrecting(false)}
                className="btn-ghost py-1.5 px-2 text-sm">
                Back
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-0.5">
              {!suggestionsLoaded && (
                <div className="text-sm text-on-surface-variant italic py-1">Matching…</div>
              )}
              {suggestionsLoaded && suggestions.length === 0 && (
                <div className="text-sm text-on-surface-variant py-1">
                  No close matches — type to search manually
                </div>
              )}
              {suggestions.map((s, i) => (
                <div
                  key={s.name}
                  onClick={() => { setSelectedIdx(i); confirmAndResume(); }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded cursor-pointer text-sm transition-colors"
                  style={{
                    background: i === selectedIdx ? 'var(--primary)' : 'transparent',
                    color: i === selectedIdx ? 'var(--on-primary)' : 'var(--on-surface)',
                  }}
                >
                  {i === selectedIdx && <span className="material-icons text-xs leading-none">check</span>}
                  <span className="flex-1">{s.name}</span>
                  <span className="text-xs opacity-50">{s.type_line?.split('—')[0].trim()}</span>
                </div>
              ))}
              <button
                type="button"
                onClick={() => { setCorrecting(true); setCorrectionValue(''); }}
                className="text-xs text-primary text-left px-3 py-1 bg-transparent border-none cursor-pointer opacity-70 hover:opacity-100 mt-0.5"
              >
                Type to search…
              </button>
            </div>
          )}
        </div>
      )}

      {/* Confirmed list */}
      {confirmed.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className="text-xs text-on-surface-variant uppercase tracking-wider font-semibold px-0.5">
            Confirmed
          </div>
          <div className="max-h-48 overflow-y-auto flex flex-col gap-0.5 pr-1">
            {confirmed.map((c, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-sm px-2 py-1 rounded"
                style={{ background: 'var(--surface-container-high)' }}
              >
                <span className="text-on-surface-variant tabular-nums text-xs w-7 text-right">{c.qty}×</span>
                <span className="flex-1 text-on-surface">{c.name}</span>
                {i === 0 && (
                  <span className="text-xs text-on-surface-variant opacity-40 mr-1">cmd</span>
                )}
                <button
                  type="button"
                  onClick={() => setConfirmed(prev => [...prev.slice(0, i), ...prev.slice(i + 1)])}
                  className="text-on-surface-variant hover:text-error-text bg-transparent border-none cursor-pointer p-0.5 leading-none"
                  title="Remove"
                >
                  <span className="material-icons" style={{ fontSize: 14 }}>close</span>
                </button>
              </div>
            ))}
            <div ref={confirmedEndRef} />
          </div>
        </div>
      )}

      {/* Done */}
      <div className="flex justify-end mt-1">
        <button
          ref={doneRef}
          type="button"
          onClick={handleDone}
          disabled={confirmed.length === 0}
          className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Done — Import {totalCards > 0 ? `${totalCards} Card${totalCards !== 1 ? 's' : ''}` : 'Deck'}
        </button>
      </div>

      {/* Idle instructions */}
      {!listening && !pending && confirmed.length <= 1 && (
        <p className="text-sm text-on-surface-variant opacity-70 text-center py-1 leading-relaxed">
          Click <strong>Start Listening</strong> then say card names one at a time.{' '}
          Say a number first for multiples: <em>"thirty"</em> then <em>"forest"</em>, or together as <em>"thirty forest"</em>.{' '}
          Say <em>"undo"</em> to remove the last card.
        </p>
      )}
    </div>
  );
}
